import {Box, Paper, Stack} from "@mui/material";
import {PANEL_GUTTER, PANEL_SLIDE_TRANSITION, SHEET_GRABBER_ROW_HEIGHT_PX} from "./useNavLayout";
import {useSlideGesture} from "./useSlideGesture";
import React, {ReactNode} from "react";

// Tall enough to show the rounded top and the grabber, so the sheet looks pullable.
const PEEK_PX = SHEET_GRABBER_ROW_HEIGHT_PX;

export interface NavigationSheetProps {
    headerHeight: number;
    contentHeight: number;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    /** Grabber and switcher. Stays on screen when the sheet is closed and is the drag handle. */
    header: ReactNode;
    children: ReactNode;
    /** False while the sheet waits out of sight below the screen for the look-around hint to finish. */
    isRevealed: boolean;
    /** Called when the visitor takes hold of the sheet, which brings it into view ahead of the hint. */
    onGrabbed: () => void;
}

/**
 * The phone portrait navigation: slides up over the panorama and can be dragged back down by its header.
 */
export function NavigationSheet(props: NavigationSheetProps) {
    const {headerHeight, contentHeight, isOpen, setIsOpen, header, children, isRevealed, onGrabbed} = props;

    // Closed leaves only the header showing; open pulls the whole sheet into view.
    const {offset, isDragging, handlers} = useSlideGesture({
        axis: "y",
        closedOffset: contentHeight,
        isOpen,
        setIsOpen,
        onTap: ({startedOpen, fromControl}) => {
            // Tapping a switcher button should reveal the section it selects and never hide it.
            if (fromControl) {
                if (!startedOpen) setIsOpen(true);
            } else {
                setIsOpen(!startedOpen);
            }
        }
    });

    // Reaching for the sheet reveals it before the drag rather than on release, so that the gesture works
    // on the sheet where it will end up instead of dragging it against the peeking offset below.
    const grab = (event: React.PointerEvent<HTMLElement>) => {
        onGrabbed();
        handlers.onPointerDown(event);
    };

    const slideDownPx = isRevealed ? offset : offset + headerHeight - PEEK_PX;

    return <Paper elevation={8}
                  sx={{
                      pointerEvents: "auto",
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: headerHeight + contentHeight,
                      display: "flex",
                      flexDirection: "column",
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      transform: `translateY(${slideDownPx}px)`,
                      // While dragging, follow the pointer exactly; animate only when settling.
                      transition: isDragging ? "none" : `transform ${PANEL_SLIDE_TRANSITION}`
                  }}>

        <Stack alignItems={"center"} justifyContent={"flex-start"}
               {...handlers}
               onPointerDown={grab}
               sx={{
                   flexShrink: 0,
                   height: headerHeight,
                   // Inset on the container, not on the children: the container has no explicit width,
                   // so padding narrows the children instead of overflowing the sheet.
                   px: PANEL_GUTTER,
                   cursor: "grab",
                   // Stops the browser claiming the vertical drag for scrolling or overscroll.
                   touchAction: "none"
               }}>
            {header}
        </Stack>

        <Box sx={{flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden"}}>
            {children}
        </Box>
    </Paper>
}
